import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import type {
  CoachAIDraftRequest,
  CoachingVisibility,
  TeamSettingsResponse,
} from "@/lib/types";
import { useTeamSettings } from "@/features/coach/hooks/useTeamSettings";
import { useCoachAIDraft } from "../hooks/useCoachAIDraft";
import type { ComposerValues } from "./Composer";

/**
 * AI drafting panel (M7.5 F1–F8) — collapsible "AI assist" toggle
 * inside the Coach Review composer. Wraps `POST /api/coach/ai/draft`.
 *
 * Three layers of UX gating mirror the backend's enforcement
 * (the backend re-checks every gate — these are hints):
 *   1. `ai.drafting_enabled` from team settings. False ⇒ toggle
 *      disabled with copy "AI drafting is disabled for this team."
 *   2. Composer's current `visibility` vs
 *      `ai.never_draft_for_visibilities`. In the set ⇒ toggle
 *      disabled, "Drafting blocked for this visibility."
 *   3. `ai.allowed_draft_targets` filters the target dropdown.
 *      Empty allow-list ⇒ panel renders empty + "Ask a team admin."
 *
 * Discoverability: even when gated, the toggle stays visible with
 * a disabled affordance + the gating copy — never hidden — so a
 * coach knows the feature exists and why it's not available.
 *
 * Privacy: the panel sends only `coach_prompt` (the inline
 * instruction) + the composer's `visibility` / `player_ids` /
 * `match_id` to the backend. Coach-typed structured fields stay
 * client-side until "Insert" appends the generated text to them.
 * Never logs / displays raw prompts or provider output beyond
 * the visible textarea + output.
 */

/** The four draft targets the composer can host. Filtered against
 * `team_settings.ai.allowed_draft_targets` at render time. */
const TARGET_OPTIONS: ReadonlyArray<{
  value: keyof Pick<
    ComposerValues,
    | "player_summary"
    | "what_happened"
    | "why_it_matters"
    | "what_to_do_next"
  >;
  backendKey: string;
  label: string;
}> = [
  { value: "player_summary", backendKey: "player_summary", label: "Player summary" },
  { value: "what_happened", backendKey: "what_happened", label: "What happened" },
  { value: "why_it_matters", backendKey: "why_it_matters", label: "Why it matters" },
  { value: "what_to_do_next", backendKey: "what_to_do_next", label: "What to do next" },
];

export type AIDraftPanelProps = {
  /** RHF instance from the Composer — read visibility/player_ids,
   * write into the target field on Insert. */
  form: UseFormReturn<ComposerValues>;
  /** Active match id (or empty for observation-only notes). */
  matchId: string;
};

export function AIDraftPanel({ form, matchId }: AIDraftPanelProps) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string>("player_summary");
  const [instruction, setInstruction] = useState("");
  const [draftedText, setDraftedText] = useState<string | null>(null);

  const settingsQuery = useTeamSettings();
  const aiDraft = useCoachAIDraft();

  // --- Gating layers ----------------------------------------------------
  const ai = readAISettings(settingsQuery.data);
  const visibility = form.watch("visibility") as CoachingVisibility;
  const isDraftingEnabled = ai.drafting_enabled;
  const isVisibilityBlocked =
    ai.never_draft_for_visibilities.includes(visibility);
  const allowedTargetKeys = new Set(ai.allowed_draft_targets);
  const availableTargets = TARGET_OPTIONS.filter((t) =>
    allowedTargetKeys.has(t.backendKey),
  );
  const hasAllowedTargets = availableTargets.length > 0;
  const gated = !isDraftingEnabled || isVisibilityBlocked || !hasAllowedTargets;

  // Pick a default target the team has enabled.
  if (!availableTargets.some((t) => t.value === target) && availableTargets[0]) {
    // Re-render-safe: state is local; this lazily fixes a stale default.
    setTimeout(() => setTarget(availableTargets[0].value), 0);
  }

  const handleGenerate = async () => {
    if (gated) return;
    const players = form.getValues("player_ids") ?? [];
    const evidenceRefs: Array<Record<string, unknown>> = matchId
      ? [{ type: "match", id: matchId }]
      : [];
    const body: CoachAIDraftRequest = {
      draft_target: target,
      target_resource_type: "player",
      target_resource_id: players[0] ?? null,
      target_visibility: visibility,
      target_player_ids: players,
      evidence_refs: evidenceRefs,
      coach_prompt: instruction || null,
    };
    try {
      const result = await aiDraft.generate(body);
      setDraftedText(result.text || "");
    } catch {
      // Error surfaces via aiDraft.error below.
      setDraftedText(null);
    }
  };

  const handleInsert = () => {
    if (!draftedText) return;
    const targetKey = target as keyof ComposerValues;
    const current = (form.getValues(targetKey) ?? "") as string;
    const next = current ? `${current}\n\n${draftedText}` : draftedText;
    form.setValue(targetKey, next, { shouldDirty: true });
  };

  const handleReset = () => {
    setDraftedText(null);
    aiDraft.reset();
  };

  // --- Render -----------------------------------------------------------
  // The toggle button always renders; gating affects only the
  // panel body + button label.
  return (
    <div
      className="flex flex-col gap-2 rounded border border-rule bg-bg-card/40 p-2"
      data-testid="ai-draft-panel"
      data-ai-gated={gated}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="ai-draft-panel-body"
        data-testid="ai-draft-panel-toggle"
        className="flex items-center justify-between gap-2 rounded px-2 py-1 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink transition-colors"
      >
        <span>AI assist {gated ? "· unavailable" : "· ready"}</span>
        <span aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>

      {open ? (
        <div
          id="ai-draft-panel-body"
          className="flex flex-col gap-2"
          data-testid="ai-draft-panel-body"
        >
          {/* Gating copy — always visible when gated, regardless
           * of whether the panel has any other content to show. */}
          {!isDraftingEnabled ? (
            <p
              className="text-ink-muted text-xs"
              data-testid="ai-draft-gate-disabled"
            >
              AI drafting is disabled for this team. Ask a team admin
              to enable it in Coach &gt; Settings.
            </p>
          ) : isVisibilityBlocked ? (
            <p
              className="text-ink-muted text-xs"
              data-testid="ai-draft-gate-visibility"
            >
              Drafting is blocked for{" "}
              <strong>{visibility}</strong>-visibility notes by the
              team's AI settings.
            </p>
          ) : !hasAllowedTargets ? (
            <p
              className="text-ink-muted text-xs"
              data-testid="ai-draft-gate-no-targets"
            >
              No draft targets are enabled for this team. Ask a team
              admin to enable at least one in Coach &gt; Settings.
            </p>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Draft target
                </span>
                <select
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  data-testid="ai-draft-target"
                  className="tktic-select text-xs"
                >
                  {availableTargets.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                  Instruction (optional)
                </span>
                <textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  maxLength={10000}
                  rows={3}
                  placeholder="Focus the draft on …"
                  data-testid="ai-draft-instruction"
                  className="bg-surface border border-rule rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </label>

              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={aiDraft.isPending}
                  data-testid="ai-draft-generate"
                  className="bg-accent hover:bg-accent-hover disabled:bg-surface disabled:text-ink-muted text-white rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors"
                >
                  {aiDraft.isPending ? "Generating…" : "Generate draft"}
                </button>
                {draftedText !== null ? (
                  <>
                    <button
                      type="button"
                      onClick={handleInsert}
                      data-testid="ai-draft-insert"
                      className="bg-surface hover:bg-bg-card border border-rule text-ink rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors"
                    >
                      Insert
                    </button>
                    <button
                      type="button"
                      onClick={handleReset}
                      data-testid="ai-draft-clear"
                      className="text-ink-muted hover:text-ink rounded px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
                    >
                      Clear
                    </button>
                  </>
                ) : null}
              </div>

              {/* M7.5 F8: per-code error copy. */}
              {aiDraft.error ? (
                <p
                  className="text-destructive font-mono text-xs"
                  data-testid="ai-draft-error"
                  data-error-code={aiDraft.error.code ?? ""}
                >
                  {errorCopy(aiDraft.error.code, aiDraft.error.message)}
                </p>
              ) : null}

              {draftedText !== null ? (
                <div
                  className="rounded border border-rule bg-surface/60 p-2"
                  data-testid="ai-draft-output"
                >
                  <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                    Draft
                  </span>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-ink">
                    {draftedText}
                  </p>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Pull the four AI settings off the team-settings GET payload,
 * with sane defaults when the query is still loading. */
function readAISettings(payload: TeamSettingsResponse | undefined): {
  drafting_enabled: boolean;
  never_draft_for_visibilities: string[];
  allowed_draft_targets: string[];
  tone: string;
} {
  const s = payload?.settings ?? {};
  return {
    drafting_enabled: s["ai.drafting_enabled"] ?? false,
    never_draft_for_visibilities: (s["ai.never_draft_for_visibilities"] ?? [
      "private",
      "player",
    ]) as string[],
    allowed_draft_targets: (s["ai.allowed_draft_targets"] ?? []) as string[],
    tone: s["ai.tone"] ?? "direct",
  };
}

/** Map the backend's structured `error_code` to actionable copy.
 * Never templates raw provider output. */
function errorCopy(code: string | undefined, fallback: string): string {
  switch (code) {
    case "drafting_disabled":
      return "AI drafting is disabled — refresh the page or ask a team admin.";
    case "target_visibility_mismatch":
      return "The composer's visibility doesn't match what this draft target requires.";
    case "unsupported_target_resource_type":
      return "This draft target isn't supported here.";
    case "prompt_too_long":
      return "Instruction is too long. Trim it and try again.";
    case "target_resource_unavailable":
      return "The target resource isn't available right now.";
    case "draft_failed":
      return "Drafting failed. Try again in a moment.";
    default:
      return fallback || "Drafting failed.";
  }
}
